import { supabase } from '../lib/supabase.js';

export class ChatManager {
  constructor() {
    this.chatSessions = [];
    this.currentMessages = [];
    this.selectedSessionId = null;
    this.isLoading = false;
    this.sessionsSubscription = null;
    this.messagesSubscription = null;
    this.filterStatus = 'open'; // Default filter
    this.sortBy = 'last_message_at'; // Default sort
    this.sortOrder = 'desc'; // Default order
  }

  render() {
    return `
      <div class="bg-white rounded-lg shadow h-screen max-h-screen overflow-hidden">
        <div class="flex h-full">
          <!-- Chat Sessions List -->
          <div class="w-1/3 border-r border-gray-200 flex flex-col">
            <div class="p-4 border-b border-gray-200 bg-gray-50">
              <div class="flex justify-between items-center">
                <h2 class="text-lg font-bold text-gray-900">Customer Chats</h2>
                <button 
                  id="refresh-chats-btn" 
                  class="bg-violet-600 text-white px-3 py-1 rounded text-sm hover:bg-violet-700"
                >
                  Refresh
                </button>
              </div>
              <p class="text-sm text-gray-600 mt-1">Manage customer conversations</p>
            </div>
            
            <!-- Filters and Sorts -->
            <div class="p-4 border-b border-gray-200 bg-gray-50 space-y-2">
              <div>
                <label for="chat-filter-status" class="block text-xs font-medium text-gray-700">Filter by Status</label>
                <select id="chat-filter-status" class="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md text-sm">
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="pending">Pending</option>
                  <option value="archived">Archived</option>
                  <option value="all">All</option>
                </select>
              </div>
              <div class="flex space-x-2">
                <div class="flex-1">
                  <label for="chat-sort-by" class="block text-xs font-medium text-gray-700">Sort By</label>
                  <select id="chat-sort-by" class="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md text-sm">
                    <option value="last_message_at">Last Message</option>
                    <option value="created_at">Created At</option>
                  </select>
                </div>
                <div class="flex-1">
                  <label for="chat-sort-order" class="block text-xs font-medium text-gray-700">Order</label>
                  <select id="chat-sort-order" class="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md text-sm">
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Sessions List -->
            <div id="sessions-list" class="flex-1 overflow-y-auto">
              <div class="p-4 text-center text-gray-500">
                <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                Loading chat sessions...
              </div>
            </div>
          </div>

          <!-- Chat Messages Area -->
          <div class="flex-1 flex flex-col">
            <!-- Chat Header -->
            <div id="chat-header" class="p-4 border-b border-gray-200 bg-gray-50 hidden">
              <div class="flex justify-between items-center">
                <div id="chat-customer-info">
                  <!-- Customer info will be populated here -->
                </div>
                <div class="flex space-x-2">
                  <button 
                    id="archive-session-btn" 
                    class="bg-yellow-500 text-white px-3 py-1 rounded text-sm hover:bg-yellow-600 disabled:opacity-50"
                    title="Archive this chat session"
                  >
                    Archive
                  </button>
                  <button 
                    id="delete-session-btn" 
                    class="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 disabled:opacity-50"
                    title="Permanently delete this chat session and all messages"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>

            <!-- Messages Area -->
            <div id="messages-area" class="flex-1 overflow-y-auto p-4 bg-gray-50">
              <div class="flex items-center justify-center h-full text-gray-500">
                <div class="text-center">
                  <div class="text-lg mb-2">💬</div>
                  <div>Select a chat to view messages</div>
                </div>
              </div>
            </div>

            <!-- Message Input Area -->
            <div id="message-input-area" class="p-4 border-t border-gray-200 bg-white hidden">
              <div class="flex space-x-3">
                <textarea 
                  id="message-input" 
                  rows="2" 
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                  placeholder="Type your message here..."
                ></textarea>
                <button 
                  id="send-message-btn" 
                  class="bg-violet-600 text-white px-4 py-2 rounded-md hover:bg-violet-700 disabled:opacity-50 self-start"
                  disabled
                >
                  Send
                </button>
              </div>
              <div class="flex justify-between items-center mt-2">
                <div class="text-xs text-gray-500">
                  Press Enter to send, Shift+Enter for new line
                </div>
                <button 
                  id="close-session-btn" 
                  class="text-xs text-red-600 hover:text-red-800"
                >
                  Close Session
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Success/Error Messages -->
        <div id="chat-notification" class="hidden fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg max-w-sm">
          <div id="chat-notification-content"></div>
        </div>
      </div>
    `;
  }

  async bindEvents() {
    // Set initial filter/sort values in UI
    document.getElementById('chat-filter-status').value = this.filterStatus;
    document.getElementById('chat-sort-by').value = this.sortBy;
    document.getElementById('chat-sort-order').value = this.sortOrder;

    // Make the manager globally accessible for notification callbacks
    window.chatManager = this;

    await this.loadChatSessions();
    
    // Start real-time subscriptions
    this.subscribeToSessions();

    // Refresh button
    document.getElementById('refresh-chats-btn').addEventListener('click', () => {
      this.loadChatSessions();
    });

    // Filter and sort change events
    document.getElementById('chat-filter-status').addEventListener('change', (e) => {
      this.filterStatus = e.target.value;
      this.loadChatSessions();
    });
    document.getElementById('chat-sort-by').addEventListener('change', (e) => {
      this.sortBy = e.target.value;
      this.loadChatSessions();
    });
    document.getElementById('chat-sort-order').addEventListener('change', (e) => {
      this.sortOrder = e.target.value;
      this.loadChatSessions();
    });

    // Message input events
    const messageInput = document.getElementById('message-input');
    const sendButton = document.getElementById('send-message-btn');

    messageInput.addEventListener('input', () => {
      sendButton.disabled = !messageInput.value.trim();
    });

    messageInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    });

    sendButton.addEventListener('click', () => {
      this.sendMessage();
    });

    // Close session button
    document.getElementById('close-session-btn').addEventListener('click', () => {
      this.closeCurrentSession();
    });

    // Archive and Delete buttons
    document.getElementById('archive-session-btn').addEventListener('click', () => {
      this.archiveCurrentSession();
    });
    document.getElementById('delete-session-btn').addEventListener('click', () => {
      this.deleteCurrentSession();
    });

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
      this.cleanup();
    });
  }

  subscribeToSessions() {
    // Subscribe to new chat sessions and updates
    this.sessionsSubscription = supabase
      .channel('chat_sessions')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'chat_sessions'
      }, (payload) => {
        // Check for new chat sessions and show notification
        if (payload.eventType === 'INSERT' && payload.new) {
          this.showNewChatNotification(payload.new);
        }
        
        // Reload sessions when any session changes
        this.loadChatSessions();
      })
      .subscribe();
  }

  showNewChatNotification(newSession) {
    // Create a prominent notification for new chats
    const notificationDiv = document.getElementById('chat-notification');
    const contentDiv = document.getElementById('chat-notification-content');
    
    if (!notificationDiv || !contentDiv) return;

    // Make the notification more prominent for new chats
    notificationDiv.className = 'fixed top-4 right-4 z-50 p-6 rounded-lg shadow-xl max-w-md bg-blue-600 text-white border-2 border-blue-800';
    contentDiv.innerHTML = `
      <div class="flex items-start">
        <div class="flex-shrink-0 mr-3">
          <div class="text-2xl">💬</div>
        </div>
        <div class="flex-1">
          <div class="font-bold text-lg mb-1">New Chat Started!</div>
          <div class="text-sm opacity-90 mb-2">
            Customer: <strong>${newSession.name}</strong><br>
            Email: <strong>${newSession.email}</strong>
          </div>
          <div class="flex space-x-2">
            <button 
              onclick="chatManager.selectSessionFromNotification('${newSession.id}')" 
              class="bg-white text-violet-600 px-3 py-1 rounded text-sm font-medium hover:bg-gray-100"
            >
              View Chat
            </button>
            <button 
              onclick="document.getElementById('chat-notification').classList.add('hidden')" 
              class="bg-violet-500 text-white px-3 py-1 rounded text-sm hover:bg-violet-400"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    `;
    
    notificationDiv.classList.remove('hidden');

    // Auto-hide after 30 seconds for new chat notifications
    setTimeout(() => {
      notificationDiv.classList.add('hidden');
    }, 30000);

    // Play a notification sound if possible
    try {
      // Create a simple beep sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      // Ignore audio errors - not all browsers support this
      console.log('Audio notification not supported');
    }
  }

  selectSessionFromNotification(sessionId) {
    // Hide the notification
    document.getElementById('chat-notification').classList.add('hidden');
    
    // Make sure we're on the right filter to see the new session
    if (this.filterStatus !== 'open' && this.filterStatus !== 'all') {
      this.filterStatus = 'open';
      document.getElementById('chat-filter-status').value = 'open';
      this.loadChatSessions().then(() => {
        this.selectSession(sessionId);
      });
    } else {
      this.selectSession(sessionId);
    }
  }

  subscribeToCurrentMessages() {
    // Unsubscribe from previous messages subscription
    if (this.messagesSubscription) {
      supabase.removeChannel(this.messagesSubscription);
    }

    if (!this.selectedSessionId) return;

    // Subscribe to new messages for the current session
    this.messagesSubscription = supabase
      .channel(`chat_messages:${this.selectedSessionId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `session_id=eq.${this.selectedSessionId}`
      }, (payload) => {
        const newMessage = payload.new;
        
        // Only add if it's not already in our current messages
        const exists = this.currentMessages.find(msg => msg.id === newMessage.id);
        if (!exists) {
          this.currentMessages.push(newMessage);
          this.renderMessages();
        }
      })
      .subscribe();
  }

  async loadChatSessions() {
    this.isLoading = true;
    
    try {
      let query = supabase
        .from('chat_sessions')
        .select('*');

      // Apply filter
      if (this.filterStatus !== 'all') {
        query = query.eq('status', this.filterStatus);
      }

      // Apply sorting
      query = query.order(this.sortBy, { ascending: this.sortOrder === 'asc' });
      query = query.order('created_at', { ascending: false }); // Secondary sort for consistency

      const { data: sessions, error } = await query;

      if (error) throw error;

      this.chatSessions = sessions || [];
      this.renderSessionsList();
    } catch (error) {
      this.showNotification('Error loading chat sessions: ' + error.message, 'error');
    } finally {
      this.isLoading = false;
    }
  }

  renderSessionsList() {
    const sessionsList = document.getElementById('sessions-list');
    
    if (this.chatSessions.length === 0) {
      sessionsList.innerHTML = `
        <div class="p-4 text-center text-gray-500">
          <div class="text-lg mb-2">💬</div>
          <div>No chat sessions found for current filters</div>
        </div>
      `;
      return;
    }

    sessionsList.innerHTML = this.chatSessions.map(session => 
      this.renderSessionCard(session)
    ).join('');

    // Bind session click events
    this.bindSessionEvents();
  }

  renderSessionCard(session) {
    const isActive = this.selectedSessionId === session.id;
    let statusColor = '';
    switch (session.status) {
      case 'open':
        statusColor = 'bg-green-100 text-green-800';
        break;
      case 'closed':
        statusColor = 'bg-gray-100 text-gray-800';
        break;
      case 'pending':
        statusColor = 'bg-yellow-100 text-yellow-800';
        break;
      case 'archived':
        statusColor = 'bg-blue-100 text-blue-800';
        break;
      default:
        statusColor = 'bg-gray-100 text-gray-800';
    }
    
    const timeAgo = this.formatTimeAgo(session.last_message_at || session.created_at);
    
    return `
      <div 
        class="session-card p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 ${isActive ? 'bg-blue-50 border-blue-200' : ''}"
        data-session-id="${session.id}"
      >
        <div class="flex justify-between items-start mb-2">
          <div class="font-medium text-gray-900 truncate">${session.name}</div>
          <span class="text-xs px-2 py-1 rounded-full ${statusColor}">${this.formatStatus(session.status)}</span>
        </div>
        <div class="text-sm text-gray-600 mb-1 truncate">${session.email}</div>
        <div class="text-xs text-gray-500">${timeAgo}</div>
      </div>
    `;
  }

  bindSessionEvents() {
    document.querySelectorAll('.session-card').forEach(card => {
      card.addEventListener('click', (e) => {
        const sessionId = e.currentTarget.dataset.sessionId;
        this.selectSession(sessionId);
      });
    });
  }

  async selectSession(sessionId) {
    this.selectedSessionId = sessionId;
    this.renderSessionsList(); // Re-render to show active session
    
    // Subscribe to messages for this session
    this.subscribeToCurrentMessages();
    
    // Show loading in messages area
    const messagesArea = document.getElementById('messages-area');
    messagesArea.innerHTML = `
      <div class="flex items-center justify-center h-full">
        <div class="text-center text-gray-500">
          <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
          Loading messages...
        </div>
      </div>
    `;

    await this.loadMessages(sessionId);
    this.showChatInterface();
  }

  async loadMessages(sessionId) {
    try {
      const { data: messages, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      this.currentMessages = messages || [];
      this.renderMessages();
    } catch (error) {
      this.showNotification('Error loading messages: ' + error.message, 'error');
    }
  }

  renderMessages() {
    const messagesArea = document.getElementById('messages-area');
    
    if (this.currentMessages.length === 0) {
      messagesArea.innerHTML = `
        <div class="flex items-center justify-center h-full text-gray-500">
          <div class="text-center">
            <div class="text-lg mb-2">💬</div>
            <div>No messages yet</div>
          </div>
        </div>
      `;
      return;
    }

    messagesArea.innerHTML = `
      <div class="space-y-4">
        ${this.currentMessages.map(message => this.renderMessage(message)).join('')}
      </div>
    `;

    // Auto-scroll to bottom
    messagesArea.scrollTop = messagesArea.scrollHeight;
  }

  renderMessage(message) {
    const isAgent = message.sender_type === 'agent';
    const timestamp = this.formatTimestamp(message.created_at);
    
    return `
      <div class="flex ${isAgent ? 'justify-end' : 'justify-start'}">
        <div class="max-w-xs lg:max-w-md">
          <div class="${isAgent ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200'} rounded-lg px-4 py-2 shadow-sm">
            <div class="text-sm">${this.escapeHtml(message.message_content)}</div>
          </div>
          <div class="text-xs text-gray-500 mt-1 ${isAgent ? 'text-right' : 'text-left'}">
            ${isAgent ? 'You' : 'Customer'} • ${timestamp}
          </div>
        </div>
      </div>
    `;
  }

  showChatInterface() {
    const session = this.chatSessions.find(s => s.id === this.selectedSessionId);
    if (!session) return;

    // Show chat header
    const chatHeader = document.getElementById('chat-header');
    const customerInfo = document.getElementById('chat-customer-info');
    const closeSessionBtn = document.getElementById('close-session-btn');
    const archiveSessionBtn = document.getElementById('archive-session-btn');
    const deleteSessionBtn = document.getElementById('delete-session-btn');
    const messageInputArea = document.getElementById('message-input-area');
    
    customerInfo.innerHTML = `
      <div class="flex justify-between items-center">
        <div>
          <div class="font-medium text-gray-900">${session.name}</div>
          <div class="text-sm text-gray-600">${session.email}</div>
        </div>
        <div class="text-right">
          <div class="text-sm font-medium">Status: ${this.formatStatus(session.status)}</div>
          <div class="text-xs text-gray-500">Started: ${this.formatTimestamp(session.created_at)}</div>
        </div>
      </div>
    `;
    
    chatHeader.classList.remove('hidden');

    // Control button visibility/enabled state based on session status
    const isSessionOpen = session.status === 'open' || session.status === 'pending';
    const isSessionArchived = session.status === 'archived';

    closeSessionBtn.disabled = !isSessionOpen;
    closeSessionBtn.classList.toggle('hidden', isSessionArchived); // Hide if archived

    archiveSessionBtn.disabled = isSessionArchived;
    if (isSessionArchived) {
      archiveSessionBtn.textContent = 'Archived';
      archiveSessionBtn.className = 'bg-gray-500 text-white px-3 py-1 rounded text-sm cursor-not-allowed opacity-50';
    } else {
      archiveSessionBtn.textContent = 'Archive';
      archiveSessionBtn.className = 'bg-yellow-500 text-white px-3 py-1 rounded text-sm hover:bg-yellow-600';
    }

    deleteSessionBtn.disabled = false; // Always allow deletion
    deleteSessionBtn.classList.remove('hidden');

    messageInputArea.classList.toggle('hidden', !isSessionOpen); // Hide input if not open/pending
    
    // Focus message input if visible
    if (isSessionOpen) {
      document.getElementById('message-input').focus();
    }
  }

  async sendMessage() {
    const messageInput = document.getElementById('message-input');
    const sendButton = document.getElementById('send-message-btn');
    const messageText = messageInput.value.trim();

    if (!messageText || !this.selectedSessionId) return;

    // Disable input while sending
    sendButton.disabled = true;
    messageInput.disabled = true;

    try {
      const { data: newMessage, error } = await supabase
        .from('chat_messages')
        .insert({
          session_id: this.selectedSessionId,
          sender_type: 'agent',
          message_content: messageText
        })
        .select()
        .single();

      if (error) throw error;

      // Clear input
      messageInput.value = '';
      
      // Add message to current messages and re-render
      this.currentMessages.push(newMessage);
      this.renderMessages();

      // Update session last_message_at and status if it was pending
      const currentSession = this.chatSessions.find(s => s.id === this.selectedSessionId);
      if (currentSession && currentSession.status === 'pending') {
        await supabase
          .from('chat_sessions')
          .update({ last_message_at: new Date().toISOString(), status: 'open' })
          .eq('id', this.selectedSessionId);
      } else {
        await supabase
          .from('chat_sessions')
          .update({ last_message_at: new Date().toISOString() })
          .eq('id', this.selectedSessionId);
      }

      // Refresh sessions list to show updated timestamp and status
      await this.loadChatSessions();
      
    } catch (error) {
      this.showNotification('Error sending message: ' + error.message, 'error');
    } finally {
      // Re-enable input
      sendButton.disabled = false;
      messageInput.disabled = false;
      messageInput.focus();
    }
  }

  async closeCurrentSession() {
    if (!this.selectedSessionId) return;

    const session = this.chatSessions.find(s => s.id === this.selectedSessionId);
    if (!session) return;

    if (!confirm(`Close chat session with ${session.name}?`)) return;

    try {
      const { error } = await supabase
        .from('chat_sessions')
        .update({ status: 'closed' })
        .eq('id', this.selectedSessionId);

      if (error) throw error;

      this.showNotification('Chat session closed successfully', 'success');
      
      // Refresh sessions
      await this.loadChatSessions();
      
      // Clear selection if the filter hides closed sessions
      if (this.filterStatus !== 'all' && this.filterStatus !== 'closed') {
        this.selectedSessionId = null;
        this.hideChatInterface();
      } else {
        // If filter allows closed, just re-render the current session to update button states
        this.showChatInterface();
      }
      
    } catch (error) {
      this.showNotification('Error closing session: ' + error.message, 'error');
    }
  }

  async archiveCurrentSession() {
    if (!this.selectedSessionId) return;

    const session = this.chatSessions.find(s => s.id === this.selectedSessionId);
    if (!session) return;

    if (session.status === 'archived') {
      this.showNotification('This session is already archived', 'warning');
      return;
    }

    if (!confirm(`Archive chat session with ${session.name}? This will hide it from the 'Open' view.`)) return;

    try {
      const { error } = await supabase
        .from('chat_sessions')
        .update({ status: 'archived' })
        .eq('id', this.selectedSessionId);

      if (error) throw error;

      this.showNotification('Chat session archived successfully', 'success');
      
      // Refresh sessions
      await this.loadChatSessions();
      
      // Clear selection if the filter hides archived sessions
      if (this.filterStatus !== 'all' && this.filterStatus !== 'archived') {
        this.selectedSessionId = null;
        this.hideChatInterface();
      } else {
        // If filter allows archived, just re-render the current session to update button states
        this.showChatInterface();
      }
      
    } catch (error) {
      this.showNotification('Error archiving session: ' + error.message, 'error');
    }
  }

  async deleteCurrentSession() {
    if (!this.selectedSessionId) return;

    const session = this.chatSessions.find(s => s.id === this.selectedSessionId);
    if (!session) return;

    if (!confirm(`WARNING: Are you sure you want to PERMANENTLY DELETE chat session with ${session.name} and ALL its messages? This action cannot be undone.`)) return;

    try {
      const { error } = await supabase
        .from('chat_sessions')
        .delete()
        .eq('id', this.selectedSessionId);

      if (error) throw error;

      this.showNotification('Chat session deleted successfully', 'success');
      
      // Refresh sessions
      await this.loadChatSessions();
      
      // Clear selection
      this.selectedSessionId = null;
      this.hideChatInterface();
      
    } catch (error) {
      this.showNotification('Error deleting session: ' + error.message, 'error');
    }
  }

  hideChatInterface() {
    document.getElementById('chat-header').classList.add('hidden');
    document.getElementById('message-input-area').classList.add('hidden');
    
    const messagesArea = document.getElementById('messages-area');
    messagesArea.innerHTML = `
      <div class="flex items-center justify-center h-full text-gray-500">
        <div class="text-center">
          <div class="text-lg mb-2">💬</div>
          <div>Select a chat to view messages</div>
        </div>
      </div>
    `;
  }

  // Cleanup method to be called when switching views
  cleanup() {
    if (this.sessionsSubscription) {
      supabase.removeChannel(this.sessionsSubscription);
      this.sessionsSubscription = null;
    }
    
    if (this.messagesSubscription) {
      supabase.removeChannel(this.messagesSubscription);
      this.messagesSubscription = null;
    }
  }

  formatTimeAgo(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  }

  formatTimestamp(dateString) {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatStatus(status) {
    switch (status) {
      case 'open': return 'Open';
      case 'closed': return 'Closed';
      case 'pending': return 'Pending';
      case 'archived': return 'Archived';
      default: return status;
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  showNotification(message, type = 'info') {
    const notificationDiv = document.getElementById('chat-notification');
    const contentDiv = document.getElementById('chat-notification-content');
    
    const colorClasses = {
      'info': 'bg-violet-50 text-violet-800 border border-violet-200',
      'success': 'bg-green-50 text-green-800 border border-green-200',
      'warning': 'bg-yellow-50 text-yellow-800 border border-yellow-200',
      'error': 'bg-red-50 text-red-800 border border-red-200'
    };

    notificationDiv.className = `fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg max-w-sm ${colorClasses[type] || colorClasses.info}`;
    contentDiv.innerHTML = `
      <div class="flex items-center">
        <div class="flex-1 text-sm">${message}</div>
        <button 
          onclick="document.getElementById('chat-notification').classList.add('hidden')" 
          class="ml-4 text-current hover:opacity-75"
        >
          ✕
        </button>
      </div>
    `;
    
    notificationDiv.classList.remove('hidden');

    // Auto-hide success and info messages after 5 seconds
    if (type === 'success' || type === 'info') {
      setTimeout(() => {
        notificationDiv.classList.add('hidden');
      }, 5000);
    }
  }
}